/* eslint-disable @typescript-eslint/no-explicit-any */
import { User } from 'firebase/auth';
import { UserProfile, Invitation, Team, TeamColorDef } from '../types';
import { TeamService } from '../services/TeamService';
import { UserService } from '../services/UserService';

interface TeamManagementProps {
    user: User | null;
    userProfile: UserProfile | null;
    allUsers: UserProfile[];
    team: Team | null;
    effectiveTeamIds: string[];
    teamColors: TeamColorDef[];
    logout: () => Promise<void>;
    setLoading: (loading: boolean) => void;
    track: (eventName: string, properties?: any) => void;
}

export const useTeamManagement = ({ 
    user, 
    userProfile, 
    allUsers, 
    effectiveTeamIds,
    teamColors,
    logout,
    setLoading,
    track
}: TeamManagementProps) => {

    const createTeam = async (name: string, visibility: 'public' | 'protected' | 'private' = 'public') => {
        if (!user || !userProfile?.orgId) return;
        
        // NORMALIZATION: Trim outer spaces AND collapse multiple internal spaces into one
        const normalizedName = name.trim().replace(/\s+/g, ' ');
        if (!normalizedName) return;

        console.log(`[Team] Creating team: "${normalizedName}" with visibility: ${visibility}`);

        // 1. Check for duplicates in the same Org
        const isDuplicate = await TeamService.isTeamNameDuplicate(userProfile.orgId, normalizedName);
        if (isDuplicate) {
            console.warn(`[Team] Duplicate name found: ${normalizedName}`);
            throw new Error(`A team named "${normalizedName}" already exists in your organization.`);
        }

        try {
            const teamRef = await TeamService.createTeam(userProfile.orgId, normalizedName, user.uid, visibility);
            track('team_created', { teamId: teamRef.id, visibility });
            console.log(`[Team] Successfully created team: ${teamRef.id}`);
        } catch (e) {
            console.error(`[Team Error] Create failed:`, e);
            throw e;
        }
    };

    const joinTeam = async (teamId: string) => {
        const trimmedId = teamId.trim();
        if (!user || !userProfile || !trimmedId) return;
        console.log(`[Team] Joining team: ${trimmedId}`);

        try {
            const teamData = await TeamService.getTeam(trimmedId);
            if (!teamData) {
                throw new Error("Team not found. Please check the ID.");
            }

            if (teamData.orgId && teamData.orgId !== userProfile.orgId) {
                throw new Error("Access Denied: Team belongs to another organization.");
            }

            if (userProfile.teamIds?.includes(trimmedId)) {
                console.log(`[Team] Already a member, switching focus.`);
                await switchTeam(trimmedId);
                return;
            }

            await TeamService.joinTeam(trimmedId, teamData.name, user.uid);
            track('team_joined', { teamId: trimmedId });
            console.log(`[Team] Successfully joined team`);
        } catch (e) {
            console.error(`[Team Error] Join failed:`, e);
            throw e;
        }
    };

    const sendInvitation = async (teamId: string, teamName: string, inviteeId: string) => {
        if (!user || !userProfile?.orgId) return;
        console.log(`[Invite] Sending invitation to ${inviteeId} for team ${teamId}`);

        // Logic character-for-character match from service
        await TeamService.sendInvitation(userProfile.orgId, teamId, teamName, user.uid, user.displayName || 'A manager', inviteeId);
        console.log(`[Invite] Successfully sent invitation`);
    };

    const inviteUserByEmail = async (email: string, teamId: string, teamName: string) => {
        console.log(`[Invite] Inviting email: ${email} to team ${teamId}`);
        try {
            await TeamService.inviteByEmail(email, teamId, teamName);
            console.log(`[Invite] Cloud function triggered for ${email}`);
        } catch (e) {
            console.error(`[Invite Error] Email invite failed:`, e);
            throw e;
        }
    };

    const acceptInvitation = async (invitation: Invitation) => {
        if (!user) return;
        setLoading(true); // START LOADING
        
        try {
            await TeamService.acceptInvitation(user.uid, invitation);
        } catch (e) {
            console.error("[Invite] Error during accept Invitation")
            // Error already logged in service, but we preserve the throw here
            throw e;
        } finally {
            setLoading(false); // ALWAYS STOP LOADING
        }
    };

    const declineInvitation = async (invitationId: string) => {
        try {
            await TeamService.declineInvitation(invitationId);
        } catch (e) {
            console.error("[Invite] Error during decline Invitation")
            throw e;
        }
    };

    const switchTeam = async (teamId: string) => {
        if (!user) return;
        console.log(`[UI] Switching active team focus to: ${teamId}`);
        track('team_switched', { teamId });
        await UserService.updateActiveTeams(user.uid, [teamId], teamId);
        console.log(`[UI] Successfully switched focus to team: ${teamId}`);
    };

    const toggleTeam = async (teamId: string) => {
        if (!user || !userProfile) return;
        const currentActive = userProfile.activeTeamIds || [];
        console.log(`[UI] Toggling team visibility: ${teamId}`);

        if (currentActive.includes(teamId)) {
            const filtered = currentActive.filter(id => id !== teamId);
            track('team_visibility_toggled', { teamId, visible: false });
            await UserService.updateActiveTeams(user.uid, filtered, filtered.length > 0 ? filtered[0] : null);
            console.log(`[UI] Successfully disabled team: ${teamId}`);
        } else {
            // New "Sanity Limit" of 50, now that technical Firestore limit of 10 is solved by chunking
            if (currentActive.length >= 50) {
                alert("You can only view up to 50 teams at once.");
                return;
            }
            const newActive = [...currentActive, teamId];
            track('team_visibility_toggled', { teamId, visible: true });
            await UserService.updateActiveTeams(user.uid, newActive, newActive[0]);
            console.log(`[UI] Successfully enabled team: ${teamId}`);
        }
    };

    const setAllTeams = async (teamIds: string[]) => {
        if (!user) return;
        console.log(`[UI] Setting visibility for all teams:`, teamIds);
        track('set_all_teams_visibility', { count: teamIds.length });
        
        // Remove hard slice to 10, relying on Sanity Limit during toggle
        const safeIds = teamIds.slice(0, 50); 
        await UserService.updateActiveTeams(user.uid, safeIds, safeIds.length > 0 ? safeIds[0] : null);
        console.log(`[UI] Successfully updated multi-team visibility`);
    };

    const leaveTeam = async (teamId: string) => {
        if (!user || !userProfile) return;
        const membership = userProfile.teams?.find(t => t.teamId === teamId);
        if (!membership) return;

        console.log(`[Team] User leaving team: ${teamId}`);
        track('team_left', { teamId });
        try {
            await TeamService.leaveTeam(teamId, user.uid, membership, effectiveTeamIds, userProfile.teams || []);
            console.log(`[Team] Successfully left team and unassigned tasks`);
        } catch (e) {
            console.error(`[Team Error] Leave failed:`, e);
            throw e;
        }
    };

    const deleteTeam = async (teamId: string) => {
        if (!user || !userProfile?.orgId) return;
        console.log(`[Team] DELETING team: ${teamId}`);
        track('team_deleted', { teamId });

        try {
            await TeamService.deleteTeam(teamId);
            console.log(`[Team] Team ${teamId} and all associated data permanently deleted`);
        } catch (e) {
            console.error(`[Team Error] Delete failed:`, e);
            throw e;
        }
    };

    const updateMemberRole = async (targetUserId: string, teamId: string, newRole: 'manager' | 'engineer') => {
        console.log(`[Team] Changing role for ${targetUserId} to ${newRole}`);
        try {
            await TeamService.updateMemberRole(targetUserId, teamId, newRole);
            console.log(`[Team] Role update successful`);
        } catch (e) { 
            console.error(`[Team Error] Role update failed:`, e);
            throw e; 
        }
    };

    const removeTeamMember = async (targetUserId: string, teamId: string) => {
        console.log(`[Team] Removing member ${targetUserId} from team ${teamId}`);
        try {
            const targetUser = allUsers.find(u => u.id === targetUserId);
            const membershipToRemove = targetUser?.teams?.find(t => t.teamId === teamId);
            if (!membershipToRemove) return;

            await TeamService.removeMember(targetUserId, teamId, membershipToRemove);
            console.log(`[Team] Member successfully removed`);
        } catch (e) { 
            console.error(`[Team Error] Member removal failed:`, e);
            throw e; 
        }
    };

    const deleteUserAccount = async () => {
        if (!user) return;
        setLoading(true);
        console.log(`[Auth] Initiating permanent account deletion for ${user.uid}...`);

        try {
            await UserService.deleteUserAccount(user);
            
            // 3. Force logout/cleanup
            await logout();
            console.log(`[Auth] Account deletion and signout complete.`);
        } catch (e: any) {
            console.error("[Auth Error] Account deletion failed", e);
            if (e.code === 'auth/requires-recent-login') {
                throw new Error("For security reasons, this action requires a recent login. Please sign out and sign back in before deleting your account.");
            }
            throw e;
        } finally {
            setLoading(false); // ALWAYS reset loading state
        }
    };

    const fetchTeamMembers = async (teamId: string): Promise<UserProfile[]> => {
        return await TeamService.fetchTeamMembers(teamId);
    };

    const getTeamName = (teamId: string) => {
        const t = userProfile?.teams?.find(tm => tm.teamId === teamId);
        return t ? t.teamName : 'Unknown Team';
    };

    const getTeamColor = (teamId: string): TeamColorDef => {
        if (!teamId) return teamColors[0];
        // Positional weighted sum to ensure better distribution for small arrays
        let charSum = 0;
        for (let i = 0; i < teamId.length; i++) {
            charSum += teamId.charCodeAt(i) * (i + 1);
        }
        const index = charSum % teamColors.length;
        return teamColors[index];
    };

    const getMyManagerTeamIds = (): string[] => {
        return userProfile?.teams
            ?.filter(t => t.role === 'manager' && effectiveTeamIds.includes(t.teamId))
            .map(t => t.teamId) || [];
    };

    return {
        createTeam, joinTeam, leaveTeam, deleteTeam, 
        sendInvitation, inviteUserByEmail, acceptInvitation, declineInvitation,
        switchTeam, toggleTeam, setAllTeams, updateMemberRole, removeTeamMember,
        deleteUserAccount, fetchTeamMembers, getTeamName, getTeamColor, getMyManagerTeamIds
    };
};
